// 上传队列：模块级 store，关闭对话框、切换页面后上传继续。一次只传一个文件（浏览器直传预签名 URL）。
import { useSyncExternalStore } from "react";
import { errorMessage } from "@/lib/api/client";
import { ep } from "@/lib/api/endpoints";

export const MAX_DEMO_BYTES = 600 * 1024 * 1024;
const EXTENSIONS = [".dem.gz", ".dem.bz2", ".dem.zst", ".zip", ".gz", ".bz2", ".dem"];
export const ACCEPT = ".dem,.gz,.bz2,.zst,.zip";

export type UploadStatus = "waiting" | "uploading" | "completing" | "queued" | "failed" | "cancelled";

export interface UploadOwner {
  type: "user" | "team";
  team_id?: string;
}

export interface UploadItem {
  key: string;
  file: File;
  name: string;
  size: number;
  owner: UploadOwner;
  status: UploadStatus;
  loaded: number;
  /** 字节 / 秒（滑动平均） */
  speed: number;
  error?: string;
  demoId?: string;
}

type Listener = () => void;

let items: UploadItem[] = [];
const listeners = new Set<Listener>();
const xhrs = new Map<string, XMLHttpRequest>();
let running = false;
let onSettled: (() => void) | null = null;

// 每次变更都替换数组引用（useSyncExternalStore 依赖引用比较）。
function emit() {
  listeners.forEach((l) => l());
}

function update(key: string, patch: Partial<UploadItem>) {
  if (!items.some((x) => x.key === key)) return;
  items = items.map((x) => (x.key === key ? { ...x, ...patch } : x));
  emit();
}

/** 返回不支持的原因；可上传时返回 null。 */
export function validateFile(f: File): string | null {
  const l = f.name.toLowerCase();
  if (!EXTENSIONS.some((e) => l.endsWith(e))) return "仅支持 .dem / .dem.gz / .dem.bz2 / .dem.zst / .zip";
  if (f.size <= 0) return "文件为空";
  if (f.size > MAX_DEMO_BYTES) return "文件超过 600 MiB";
  return null;
}

let seq = 0;

export function enqueue(files: File[], owner: UploadOwner) {
  const added: UploadItem[] = [];
  for (const f of files) {
    const err = validateFile(f);
    added.push({
      key: `${Date.now()}-${seq++}`,
      file: f,
      name: f.name,
      size: f.size,
      owner,
      status: err ? "failed" : "waiting",
      loaded: 0,
      speed: 0,
      error: err ?? undefined,
    });
  }
  items = [...items, ...added];
  emit();
  void pump();
}

export async function cancel(key: string) {
  const it = items.find((x) => x.key === key);
  if (!it) return;
  if (it.status === "waiting") {
    update(key, { status: "cancelled" });
    return;
  }
  if (it.status === "uploading") {
    xhrs.get(key)?.abort();
    update(key, { status: "cancelled" });
    if (it.demoId) await ep.uploadAbort(it.demoId).catch(() => undefined);
  }
}

/** 从列表移除（已完成 / 失败 / 已取消的项；等待中的项同时取消）。 */
export function remove(key: string) {
  const it = items.find((x) => x.key === key);
  if (it && (it.status === "uploading" || it.status === "waiting")) void cancel(key);
  items = items.filter((x) => x.key !== key);
  emit();
}

export function setOnSettled(fn: (() => void) | null) {
  onSettled = fn;
}

export const hasActiveUploads = () => items.some((x) => x.status === "waiting" || x.status === "uploading" || x.status === "completing");

function putFile(it: UploadItem, url: string, method: string, headers: Record<string, string>): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhrs.set(it.key, xhr);
    xhr.open(method || "PUT", url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    let lastT = performance.now();
    let lastLoaded = 0;
    let speed = 0;
    xhr.upload.onprogress = (e) => {
      const now = performance.now();
      const dt = (now - lastT) / 1000;
      if (dt >= 0.5) {
        const inst = (e.loaded - lastLoaded) / dt;
        speed = speed ? speed * 0.7 + inst * 0.3 : inst;
        lastT = now;
        lastLoaded = e.loaded;
      }
      update(it.key, { loaded: e.loaded, speed });
    };
    xhr.onload = () => {
      xhrs.delete(it.key);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`上传失败（HTTP ${xhr.status}）`));
    };
    xhr.onerror = () => {
      xhrs.delete(it.key);
      reject(new Error("网络错误，上传中断"));
    };
    xhr.onabort = () => {
      xhrs.delete(it.key);
      reject(new DOMException("aborted", "AbortError"));
    };
    xhr.send(it.file);
  });
}

async function pump() {
  if (running) return;
  running = true;
  try {
    for (;;) {
      const it = items.find((x) => x.status === "waiting");
      if (!it) break;
      update(it.key, { status: "uploading", loaded: 0, speed: 0 });
      try {
        const init = await ep.uploadInit({ owner: it.owner, filename: it.name, size: it.size });
        update(it.key, { demoId: init.demo.id });
        if (items.find((x) => x.key === it.key)?.status === "cancelled") {
          await ep.uploadAbort(init.demo.id).catch(() => undefined);
          continue;
        }
        await putFile(it, init.upload.url, init.upload.method, init.upload.headers ?? {});
        update(it.key, { status: "completing", loaded: it.size });
        await ep.uploadComplete(init.demo.id);
        update(it.key, { status: "queued" });
        onSettled?.();
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") continue;
        const cur = items.find((x) => x.key === it.key);
        if (cur?.status !== "cancelled") update(it.key, { status: "failed", error: errorMessage(e, "上传失败") });
        // PUT 失败时撤销服务端的待上传记录，避免列表里残留「等待上传」。
        if (cur?.demoId && cur.status === "uploading") await ep.uploadAbort(cur.demoId).catch(() => undefined);
        if (cur?.demoId) onSettled?.();
      }
    }
  } finally {
    running = false;
  }
}

function subscribe(l: Listener) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useUploads(): UploadItem[] {
  return useSyncExternalStore(subscribe, () => items);
}
